// src/pages/AssignPoints/StudentSelectPage.js
// Students selector for Award Points flow.
// - Table with checkboxes (multi-select)
// - Search, house, grade filters
// - Pagination + "select all on page"
// - Next → navigates to /assign/reasons with selected IDs in location.state

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { studentsApi } from "../../api/studentsApi";
import { api } from "../../api/client";

// Seed the grade dropdown with 1–12 so Grade 1 is always available
const GRADE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

// --- Grade normalizer: "1", "01", "1st", "Grade 1" -> 1; null if invalid/out of range
function normalizeGrade(input) {
  if (input === null || input === undefined) return null;
  const raw = String(input).trim();
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    if (n < 0 || n > 12) return null; // adjust if your school differs
    return n;
  }
  const digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return null;
  const n = Number(digits);
  if (Number.isNaN(n)) return null;
  if (n < 0 || n > 12) return null;
  return n;
}

export default function StudentSelectPage() {
  const navigate = useNavigate();

  // data + ui state
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // filters + paging
  const [search, setSearch] = useState("");
  const [houseId, setHouseId] = useState(""); // send id, not name
  const [grade, setGrade] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // dropdown data
  const [houses, setHouses] = useState([]); // [{id,name}]
  const [allGrades, setAllGrades] = useState(GRADE_OPTIONS); // ✅ seed with 1–12

  const debouncedSearch = useDebounced(search, 300);

  const query = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (debouncedSearch) p.q = debouncedSearch;
    if (houseId) p.house_id = houseId; // backend expects house_id
    if (grade) {
      const ng = normalizeGrade(grade);
      if (ng !== null) p.grade = ng; // SEND NUMERIC
      else p.grade = grade; // fallback (won't break)
    }
    return p;
  }, [debouncedSearch, houseId, grade, page, pageSize]);

  // selection (persist across pagination)
  const [selected, setSelected] = useState(() => new Set());
  const isSelected = (id) => selected.has(String(id));
  const toggleOne = (id) => {
    const key = String(id);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };
  const togglePage = (ids, allChecked) => {
    const next = new Set(selected);
    if (allChecked) ids.forEach((id) => next.delete(String(id)));
    else ids.forEach((id) => next.add(String(id)));
    setSelected(next);
  };

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const res = await studentsApi.list(query);
      if (!res.ok) {
        setRows([]);
        setCount(0);
        setErr(`Failed to load students (${res.status}).`);
        return;
      }
      setRows(res.students);
      setCount(typeof res.count === "number" ? res.count : res.students.length);
    } catch (e) {
      setRows([]);
      setCount(0);
      setErr(e?.message || "Unexpected error.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(query)]);

  // load Houses from public endpoint (force no auth header)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const h = await api.get("/api/houses/summary", { auth: false });
        const results = Array.isArray(h?.data?.results) ? h.data.results : [];
        if (alive) {
          setHouses(results.map((r) => ({ id: String(r.id), name: r.name })));
        }
      } catch {
        // "All Houses" still works
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Build Grade dropdown from loaded rows (accumulate as user browses)
  useEffect(() => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    setAllGrades((prev) => {
      const set = new Set(prev);
      rows.forEach((r) => {
        const raw = (r.grade ?? r.grade_level ?? "").toString().trim();
        const ng = normalizeGrade(raw);
        if (ng !== null) set.add(String(ng)); // store as de-duped normalized string
      });
      const arr = Array.from(set);
      arr.sort((a, b) => Number(a) - Number(b));
      return arr;
    });
  }, [rows]);

  // header checkbox indeterminate wiring (✅ added)
  const headerRef = useRef(null);
  const visibleIds = rows.map((r) => r.id).filter((id) => id !== null && id !== undefined);
  const allOnPageChecked =
    visibleIds.length > 0 && visibleIds.every((id) => isSelected(id));
  const someOnPageChecked =
    !allOnPageChecked && visibleIds.some((id) => isSelected(id));
  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = someOnPageChecked;
  }, [someOnPageChecked]);

  const goNext = () => {
    if (selected.size === 0) return;
    navigate("/assign/reasons", {
      state: { studentIds: Array.from(selected) }, // keep your key
    });
  };

  const totalPages =
    count && pageSize ? Math.max(1, Math.ceil(count / pageSize)) : null;

  return (
    <div className="px-4 py-4 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Assign Points → Select Students</h1>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <input
          className="border rounded px-3 py-2 text-base"
          placeholder="Search by name"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />

        <select
          className="border rounded px-3 py-2 text-base"
          value={houseId}
          onChange={(e) => {
            setPage(1);
            setHouseId(e.target.value);
          }}
        >
          <option value="">All Houses</option>
          {houses.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name}
            </option>
          ))}
        </select>

        <select
          className="border rounded px-3 py-2 text-base"
          value={grade}
          onChange={(e) => {
            setPage(1);
            setGrade(e.target.value);
          }}
        >
          <option value="">All Grades</option>
          {allGrades.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>

        <div className="flex gap-2">
          <button
            className="border rounded px-3 py-2"
            onClick={() => {
              setPage(1);
              load();
            }}
          >
            Refresh
          </button>
          <button
            className="border rounded px-3 py-2"
            onClick={() => {
              setSearch("");
              setHouseId("");
              setGrade("");
              setPage(1);
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Selection summary + paging controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
        <div className="text-sm text-gray-600">
          {loading
            ? "Loading…"
            : `Showing ${rows.length}${
                typeof count === "number" ? ` of ${count}` : ""
              } students`}
          {selected.size > 0 && (
            <span className="ml-2 font-semibold">
              • Selected: {selected.size}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-sm">Page Size</label>
          <select
            className="border rounded px-2 py-1"
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(Number(e.target.value));
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button
            className="border rounded px-2 py-1"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <button
            className="border rounded px-2 py-1"
            disabled={
              loading ||
              (totalPages ? page >= totalPages : rows.length < pageSize)
            }
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {/* Error */}
      {err && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded p-3 mb-3">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="overflow-auto border rounded">
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            fontSize: 14,
            tableLayout: "fixed", // match Reports behavior
            minWidth: 560, // ✅ readable columns on small screens
          }}
        >
          {/* Reserve explicit widths: checkbox 3rem, name auto, house 12rem, grade 8rem */}
          <colgroup>
            <col style={{ width: "3rem" }} />
            <col />
            <col style={{ width: "12rem" }} />
            <col style={{ width: "8rem" }} />
          </colgroup>
          <thead>
            <tr>
              <th
                style={{
                  textAlign: "center",
                  background: "#F5F8FB",
                  color: "#003e73",
                  padding: "10px 12px",
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <input
                  type="checkbox"
                  aria-label="Select all on page"
                  checked={allOnPageChecked}
                  ref={headerRef}
                  onChange={() => togglePage(visibleIds, allOnPageChecked)}
                />
              </th>
              <th
                style={{
                  textAlign: "left",
                  background: "#F5F8FB",
                  color: "#003e73",
                  padding: "10px 12px",
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                Name
              </th>
              <th
                style={{
                  textAlign: "left",
                  background: "#F5F8FB",
                  color: "#003e73",
                  padding: "10px 12px",
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                House
              </th>
              <th
                style={{
                  textAlign: "left",
                  background: "#F5F8FB",
                  color: "#003e73",
                  padding: "10px 12px",
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                Grade
              </th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && !err && (
              <tr>
                <td
                  colSpan={4}
                  style={{
                    padding: "10px 12px",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    color: "#4b5563",
                  }}
                >
                  No students found for the current filters.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <td
                  style={{
                    padding: "10px 12px",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    textAlign: "center",
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={`Select ${r.name}`}
                    checked={isSelected(r.id)}
                    onChange={() => toggleOne(r.id)}
                  />
                </td>
                <td
                  style={{
                    padding: "10px 12px",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={r.name}
                >
                  {r.name}
                </td>
                <td
                  style={{
                    padding: "10px 12px",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.house || "—"}
                </td>
                <td
                  style={{
                    padding: "10px 12px",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.grade ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer actions */}
      <div className="mt-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <button
          className="border rounded px-3 py-2"
          onClick={() => setSelected(new Set())}
          disabled={selected.size === 0}
        >
          Clear Selection
        </button>
        <button
          className="border rounded px-4 py-2 bg-emerald-600 text-white disabled:opacity-50"
          onClick={goNext}
          disabled={selected.size === 0}
        >
          Next ({selected.size})
        </button>
      </div>
    </div>
  );
}
